import { FormEvent, useEffect, useRef, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faPen, faPhone, faTrash, faUser } from '@fortawesome/free-solid-svg-icons'
import type { Core, FieldOptions } from '@form-validation/core'
import { ViewportScroll } from '@/components/common/EditTableScroll'
import { useAuth } from '@/context/AuthContext'
import { useCrmHub } from '@/context/SignalRContext'
import { createFormValidation, isFormValid } from '@/helpers/formValidationHelper'
import type { Contact, ContactDetail, NamedItem } from '@/models/api'
import { readError } from '@/services/apiClient'
import { deleteContact, loadContacts, saveContact } from '@/services/contactService'
import { loadReferenceData } from '@/services/referenceService'

interface DetailDraft {
  key: string
  contactMethodId: string
  detail: string
  preferred: boolean
}

const emptyDetail = (): DetailDraft => ({ key: crypto.randomUUID(), contactMethodId: '', detail: '', preferred: false })

export function CustomerContacts({ customerId, customerName, canEdit }: { customerId: string; customerName: string; canEdit: boolean }) {
  const { session } = useAuth()
  const { notify } = useCrmHub()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Contact | null | undefined>(undefined)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function reload() {
    return loadContacts(customerId).then(setContacts).catch((caught) => setError(readError(caught)))
  }

  useEffect(() => {
    let active = true
    loadContacts(customerId)
      .then((rows) => { if (active) setContacts(rows) })
      .catch((caught) => { if (active) setError(readError(caught)) })
    return () => { active = false }
  }, [customerId])

  async function remove(contact: Contact) {
    setBusy(true)
    setError(null)
    try {
      await deleteContact(contact.id)
      await notify('SendCompanyContactDeleted', { contactId: contact.id, customerId, userId: session?.userId })
      setConfirmId(null)
      await reload()
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      {canEdit && <Button className="mb-3" onClick={() => { setEditing(null); setConfirmId(null) }}>+ Add contact</Button>}
      <ViewportScroll className="activity-scroll">
        {contacts.length === 0 && <p className="text-body-secondary">No contacts recorded yet.</p>}
        <div className="contact-grid">
          {contacts.map((contact) => (
            <article className="contact-card" key={contact.id}>
              {canEdit && (
                <header>
                  {confirmId === contact.id ? (
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="danger" disabled={busy} onClick={() => void remove(contact)}>Delete contact</Button>
                      <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setConfirmId(null)}>Keep</Button>
                    </div>
                  ) : (
                    <>
                      <button type="button" className="btn btn-outline-primary btn-sm contact-card__icon" aria-label="Edit contact" onClick={() => { setEditing(contact); setConfirmId(null) }}><FontAwesomeIcon icon={faPen} /></button>
                      <button type="button" className="btn btn-outline-danger btn-sm contact-card__icon" aria-label="Delete contact" onClick={() => setConfirmId(contact.id)}><FontAwesomeIcon icon={faTrash} /></button>
                    </>
                  )}
                </header>
              )}
              <p className="contact-card__name"><FontAwesomeIcon icon={faUser} /> {contact.fullname || [contact.title, contact.firstname, contact.lastname].filter(Boolean).join(' ')}</p>
              {contact.position && <p className="mb-0">{contact.position}</p>}
              <p className="text-body-secondary mb-2">{contact.organisationName || customerName}{contact.defaultContact ? ' · Default' : ''}</p>
              <div className="contact-card__details">
                {visibleDetails(contact).map((detail) => (
                  <p key={detail.id}>
                    <FontAwesomeIcon icon={detail.methodType === 'Email' ? faEnvelope : faPhone} />
                    {detail.methodType === 'Email'
                      ? <a href={`mailto:${detail.detail}`}>{detail.detail}</a>
                      : <a href={`tel:${detail.detail}`}>{detail.detail}</a>}
                    <span className="text-body-secondary"> · {detailLabel(detail)}</span>
                  </p>
                ))}
              </div>
              {contact.background && <p className="contact-card__note">{contact.background}</p>}
            </article>
          ))}
        </div>
      </ViewportScroll>
      {editing !== undefined && (
        <ContactDialog
          customerId={customerId}
          contact={editing}
          onClose={() => setEditing(undefined)}
          onSaved={async (created) => {
            await notify(created ? 'SendCompanyContactAdded' : 'SendCompanyContactUpdated', { customerId, contactId: editing?.id, userId: session?.userId })
            await reload()
          }}
        />
      )}
    </>
  )
}

function visibleDetails(contact: Contact) {
  if (contact.details?.length) return contact.details.filter((detail) => detail.detail)
  const fallback: ContactDetail[] = []
  if (contact.defaultOrFirstEmailAddress) fallback.push({ id: 'email', contactMethodId: 0, method: 'Email', methodType: 'Email', detail: contact.defaultOrFirstEmailAddress, default: true })
  if (contact.defaultOrFirstContactNumber) fallback.push({ id: 'phone', contactMethodId: 0, method: 'Phone', methodType: 'Phone', detail: contact.defaultOrFirstContactNumber, default: true })
  return fallback
}

function detailLabel(detail: ContactDetail) {
  const parts = (detail.method?.trim() || detail.methodType?.trim() || 'Contact')
    .split(/[()]+/)
    .map(part => part.trim())
    .filter(Boolean)
  return parts.filter((part, index) => parts.findIndex(value => value.toLowerCase() === part.toLowerCase()) === index).join(' · ')
}

function detailValidators(): FieldOptions {
  return {
    validators: {
      notEmpty: { message: 'Contact detail required' },
      callback: {
        message: 'Enter a valid email address',
        callback: (input: { value?: unknown; element?: HTMLElement }) => {
          const select = input.element?.closest('.row')?.querySelector('select')
          const type = select instanceof HTMLSelectElement ? select.selectedOptions[0]?.dataset.methodType : undefined
          if (type !== '2') return true
          const value = String(input.value ?? '').trim()
          if (!value) return true
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        },
      },
    },
  }
}

function ContactDialog({
  customerId,
  contact,
  onClose,
  onSaved,
}: {
  customerId: string
  contact: Contact | null
  onClose: () => void
  onSaved: (created: boolean) => Promise<void>
}) {
  const formRef = useRef<HTMLFormElement>(null)
  const fvRef = useRef<Core | null>(null)
  const [titles, setTitles] = useState<NamedItem[]>([])
  const [methods, setMethods] = useState<NamedItem[]>([])
  const [titleId, setTitleId] = useState(contact?.titleId ? String(contact.titleId) : '')
  const [firstname, setFirstname] = useState(contact?.firstname ?? '')
  const [lastname, setLastname] = useState(contact?.lastname ?? '')
  const [position, setPosition] = useState(contact?.position ?? '')
  const [background, setBackground] = useState(contact?.background ?? '')
  const [defaultContact, setDefaultContact] = useState(contact?.defaultContact ?? false)
  const [details, setDetails] = useState<DetailDraft[]>(() => contact?.details?.map((detail) => ({
    key: String(detail.id),
    contactMethodId: String(detail.contactMethodId),
    detail: detail.detail,
    preferred: detail.default,
  })) ?? [])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const detailKeys = details.map((row) => row.key).join('|')

  useEffect(() => {
    Promise.all([loadReferenceData('title-prefixes'), loadReferenceData('contact-methods')])
      .then(([nextTitles, nextMethods]) => {
        setTitles(nextTitles.filter((item) => item.enabled))
        setMethods(nextMethods.filter((item) => item.enabled))
      })
      .catch((caught) => setError(readError(caught)))
  }, [])

  useEffect(() => {
    const element = formRef.current
    if (!element) return
    const fields: Record<string, FieldOptions> = {
      titleId: { validators: { notEmpty: { message: 'Title required' } } },
      firstname: { validators: { notEmpty: { message: 'First name required' } } },
      lastname: { validators: { notEmpty: { message: 'Last name required' } } },
    }
    for (const key of detailKeys.split('|').filter(Boolean)) {
      fields[`method-${key}`] = { validators: { notEmpty: { message: 'Contact method required' } } }
      fields[`detail-${key}`] = detailValidators()
    }
    const fv = createFormValidation(element, { submitOnly: true, fields })
    fvRef.current = fv
    return () => { fv.destroy(); fvRef.current = null }
  }, [detailKeys])

  function patchDetail(key: string, next: Partial<DetailDraft>) {
    setDetails((current) => current.map((row) => row.key === key ? { ...row, ...next } : row))
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!(await isFormValid(fvRef.current))) return
    setBusy(true)
    setError(null)
    try {
      await saveContact({
        id: contact?.id ?? null,
        customerId,
        titleId: Number(titleId),
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        position: position.trim() || null,
        background: background.trim() || null,
        defaultContact,
        details: details.map((row) => ({ contactMethodId: Number(row.contactMethodId), detail: row.detail.trim(), default: row.preferred })),
      })
      await onSaved(!contact)
      onClose()
    } catch (caught) {
      setError(readError(caught))
      setBusy(false)
    }
  }

  return (
    <Modal show onHide={onClose} size="lg">
      <Form ref={formRef} noValidate onSubmit={(event) => void submit(event)}>
        <Modal.Header closeButton><Modal.Title>{contact ? 'Edit contact' : 'Add contact'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="contact-position">
                <Form.Label>Position</Form.Label>
                <Form.Control value={position} onChange={(event) => setPosition(event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="contact-title">
                <Form.Label>Title</Form.Label>
                <Form.Select name="titleId" value={titleId} onChange={(event) => setTitleId(event.target.value)}>
                  <option value="">Select a title</option>
                  {titles.map((item) => <option key={item.id} value={item.id} data-method-type={item.methodType}>{item.name}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="contact-firstname">
                <Form.Label>First name</Form.Label>
                <Form.Control name="firstname" value={firstname} onChange={(event) => setFirstname(event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="contact-lastname">
                <Form.Label>Last name</Form.Label>
                <Form.Control name="lastname" value={lastname} onChange={(event) => setLastname(event.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Switch id="contact-default" className="mb-3" label="default for company" checked={defaultContact} onChange={(event) => setDefaultContact(event.target.checked)} />
          <Form.Group className="mb-3" controlId="contact-background">
            <Form.Label>Background Notes</Form.Label>
            <Form.Control as="textarea" rows={3} value={background} onChange={(event) => setBackground(event.target.value)} />
          </Form.Group>
          <Button type="button" variant="outline-primary" className="mb-3" onClick={() => setDetails((current) => [...current, emptyDetail()])}>+ Add Contact Detail</Button>
          {details.length > 0 && (
            <div className="border rounded-3 p-3">
              {details.map((row) => (
                <Row key={row.key} className="g-2 mb-2 align-items-start">
                  <Col md={4}>
                    <Form.Select name={`method-${row.key}`} aria-label="Contact method" value={row.contactMethodId} onChange={(event) => patchDetail(row.key, { contactMethodId: event.target.value })}>
                      <option value="">Contact method</option>
                      {methods.map((item) => <option key={item.id} value={item.id} data-method-type={item.methodType}>{item.name}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Control name={`detail-${row.key}`} aria-label="Contact detail" value={row.detail} onChange={(event) => patchDetail(row.key, { detail: event.target.value })} />
                  </Col>
                  <Col md={4} className="d-flex align-items-center gap-2">
                    <Form.Check id={`contact-default-detail-${row.key}`} className="mb-0" label="Set Default" checked={row.preferred} onChange={(event) => patchDetail(row.key, { preferred: event.target.checked })} />
                    <button type="button" className="btn btn-outline-danger btn-sm contact-card__icon ms-auto" aria-label="Remove detail" onClick={() => setDetails((current) => current.filter((item) => item.key !== row.key))}><FontAwesomeIcon icon={faTrash} /></button>
                  </Col>
                </Row>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy}>Save</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
